/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddMissionToGroupInput } from '../models/AddMissionToGroupInput';
import type { AddResourceInput } from '../models/AddResourceInput';
import type { CreateActionInput } from '../models/CreateActionInput';
import type { CreateMissionInput } from '../models/CreateMissionInput';
import type { CreateReportInput } from '../models/CreateReportInput';
import type { FileUploadRequest } from '../models/FileUploadRequest';
import type { FileUploadResponse } from '../models/FileUploadResponse';
import type { FileURL } from '../models/FileURL';
import type { ID } from '../models/ID';
import type { MapElementData } from '../models/MapElementData';
import type { Mission } from '../models/Mission';
import type { MissionEventsList } from '../models/MissionEventsList';
import type { MissionGroup } from '../models/MissionGroup';
import type { MissionGroupData } from '../models/MissionGroupData';
import type { MissionGroupList } from '../models/MissionGroupList';
import type { MissionsList } from '../models/MissionsList';
import type { PatientUpdate } from '../models/PatientUpdate';
import type { PostMissionInput } from '../models/PostMissionInput';
import type { SectionData } from '../models/SectionData';
import type { UpdateMissionInput } from '../models/UpdateMissionInput';
import type { UpdateResourceInput } from '../models/UpdateResourceInput';
import type { UUID } from '../models/UUID';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class MissionsService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Create or update a mission
   * Create or update a missions based on an external identifier.
   *
   * The preferred method to create or update missions is `PUT /missions/{id}`.
   * But if your architecture does not allow you to maintain a mapping between
   * your system's mission IDs and those from this API, you can use this `POST`
   * method.
   *
   * We identify missions with the combination of your API key and the mission
   * property `externalId`. The mapping will be kept for at least 24 hours after
   * the last update to the mission.
   *
   * If you pass the property `groupIds`, the mission will only be created for
   * these mission groups. Otherwise it will be visible in all groups that you
   * have write access to.
   *
   * Note that this operation completely replaces the state of the mission. Any
   * existing resources, actions or reports that are not present in the payload
   * will be removed from the mission.
   *
   * @param requestBody
   * @returns Mission The mission was successfully created or updated.
   *
   * @throws ApiError
   */
  public postMission(
    requestBody: PostMissionInput,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/missions',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        409: `An mission with this ID already exists or is not active anymore.
        `,
      },
    });
  }

  /**
   * Get a mission
   * @param missionId The ID of the mission.
   * @returns Mission The mission data.
   * @throws ApiError
   */
  public getMission(
    missionId: UUID,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/missions/{missionId}',
      path: {
        'missionId': missionId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Create or update a mission
   * Create a new mission or update the values of an active mission.
   *
   * If you pass the property `groupIds`, the mission will only be created for
   * these mission groups. Otherwise it will be visible in all groups that you
   * have write access to.
   *
   * Note that this operation completely replaces the state of the mission. Any
   * existing resources, actions or reports that are not present in the payload
   * will be removed from the mission. If you just want to update certain fields
   * of the mission, use `PATCH /missions/{missionId}` instead.
   *
   * @param missionId The ID of the mission.
   * @param requestBody
   * @returns Mission The mission was successfully created or updated.
   *
   * @throws ApiError
   */
  public createMission(
    missionId: UUID,
    requestBody: CreateMissionInput,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}',
      path: {
        'missionId': missionId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        409: `An mission with this ID already exists or is not active anymore.
        `,
      },
    });
  }

  /**
   * Update details of an active mission
   * @param missionId The ID of the mission.
   * @param requestBody
   * @returns Mission The mission was successfully updated.
   *
   * @throws ApiError
   */
  public updateMission(
    missionId: UUID,
    requestBody: UpdateMissionInput,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/missions/{missionId}',
      path: {
        'missionId': missionId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * End an active mission
   * @param missionId The ID of the mission.
   * @returns void
   * @throws ApiError
   */
  public endMission(
    missionId: UUID,
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/missions/{missionId}',
      path: {
        'missionId': missionId,
      },
      errors: {
        404: `There is no active mission with the given ID. Possibly the mission was already ended.
        `,
      },
    });
  }

  /**
   * Get a PDF report of all resources at the current time for a mission.
   * @param missionId The ID of the mission.
   * @returns binary The mission events.
   * @throws ApiError
   */
  public getMissionResourcesReport(
    missionId: UUID,
  ): CancelablePromise<Blob> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/missions/{missionId}/reports/resources',
      path: {
        'missionId': missionId,
      },
    });
  }

  /**
   * Get all events that happened during a mission
   * @param missionId The ID of the mission.
   * @returns MissionEventsList The mission events.
   * @throws ApiError
   */
  public getMissionEvents(
    missionId: UUID,
  ): CancelablePromise<MissionEventsList> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/missions/{missionId}/events',
      path: {
        'missionId': missionId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Report an action taken on a mission
   * @param missionId The ID of the mission
   * @param actionId The ID of the action to create
   * @param requestBody
   * @returns Mission The action was successfully created.
   * @throws ApiError
   */
  public createMissionAction(
    missionId: UUID,
    actionId: ID,
    requestBody: CreateActionInput,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}/actions/{actionId}',
      path: {
        'missionId': missionId,
        'actionId': actionId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
        409: `The mission already contains an action with the given ID but different values.
        Note that actions are immutable.
        `,
      },
    });
  }

  /**
   * Add a report to a mission
   * @param missionId The ID of the mission
   * @param reportId The ID of the report to create.
   * @param requestBody
   * @returns Mission The report was successfully created.
   * @throws ApiError
   */
  public createMissionReport(
    missionId: UUID,
    reportId: ID,
    requestBody: CreateReportInput,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}/reports/{reportId}',
      path: {
        'missionId': missionId,
        'reportId': reportId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
        409: `The mission already contains an report with the given ID but different values.
        Note that reports are immutable.
        `,
      },
    });
  }

  /**
   * Get a binary file
   * @param missionId The ID of the mission
   * @param fileId The ID of the file to create.
   * @param noRedirect
   * @returns FileURL The URL to the file.
   * @throws ApiError
   */
  public getFile(
    missionId: UUID,
    fileId: ID,
    noRedirect?: boolean,
  ): CancelablePromise<FileURL> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/missions/{missionId}/files/{fileId}',
      path: {
        'missionId': missionId,
        'fileId': fileId,
      },
      query: {
        'noRedirect': noRedirect,
      },
      errors: {
        302: `The object is found.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Request to upload a binary file.
   * @param missionId The ID of the mission
   * @param fileId The ID of the file to create.
   * @param requestBody
   * @returns FileUploadResponse The file was successfully registered. Use the returned URL to upload the file using a PUT request.
   * @throws ApiError
   */
  public requestFileUpload(
    missionId: UUID,
    fileId: ID,
    requestBody: FileUploadRequest,
  ): CancelablePromise<FileUploadResponse> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}/files/{fileId}',
      path: {
        'missionId': missionId,
        'fileId': fileId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Add a resource to a mission
   * @param missionId The ID of the mission
   * @param resourceId The ID of the resource to add.
   * @param requestBody
   * @returns Mission The resource was successfully added to the mission.
   * @throws ApiError
   */
  public createMissionResource(
    missionId: UUID,
    resourceId: ID,
    requestBody: AddResourceInput,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}/resources/{resourceId}',
      path: {
        'missionId': missionId,
        'resourceId': resourceId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
        409: `The mission already contains a resource with the given ID but different values.
        `,
      },
    });
  }

  /**
   * Update the state of a resource
   * @param missionId The ID of the mission
   * @param resourceId The ID of the resource.
   * @param requestBody
   * @returns Mission The resource was successfully updated.
   * @throws ApiError
   */
  public updateMissionResource(
    missionId: UUID,
    resourceId: ID,
    requestBody: UpdateResourceInput,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/missions/{missionId}/resources/{resourceId}',
      path: {
        'missionId': missionId,
        'resourceId': resourceId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Remove a resource from a mission
   * @param missionId The ID of the mission
   * @param resourceId The ID of the resource to remove
   * @returns Mission The resource was successfully remove from the mission.
   * @throws ApiError
   */
  public removeMissionResource(
    missionId: UUID,
    resourceId: ID,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/missions/{missionId}/resources/{resourceId}',
      path: {
        'missionId': missionId,
        'resourceId': resourceId,
      },
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Create a new map element for a mission.
   * @param missionId The ID of the mission
   * @param elementId The ID of the new map element
   * @param requestBody
   * @returns Mission The map element was successfully added to the mission.
   * @throws ApiError
   */
  public createMissionMapElement(
    missionId: UUID,
    elementId: ID,
    requestBody: MapElementData,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}/map-elements/{elementId}',
      path: {
        'missionId': missionId,
        'elementId': elementId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Delete a map element for a mission.
   * @param missionId The ID of the mission
   * @param elementId The ID of the new map element
   * @returns Mission The map element was successfully deleted from the mission.
   * @throws ApiError
   */
  public removeMissionMapElement(
    missionId: UUID,
    elementId: ID,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/missions/{missionId}/map-elements/{elementId}',
      path: {
        'missionId': missionId,
        'elementId': elementId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Create a new section for a mission.
   * @param missionId The ID of the mission
   * @param sectionId The ID of the new section
   * @param requestBody
   * @returns Mission The section was successfully added to the mission.
   * @throws ApiError
   */
  public createMissionSection(
    missionId: UUID,
    sectionId: ID,
    requestBody: SectionData,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}/sections/{sectionId}',
      path: {
        'missionId': missionId,
        'sectionId': sectionId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Update an existing section.
   * @param missionId The ID of the mission
   * @param sectionId The ID of the section
   * @param requestBody
   * @returns Mission The section was successfully updated.
   * @throws ApiError
   */
  public updateMissionSection(
    missionId: UUID,
    sectionId: ID,
    requestBody: SectionData,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/missions/{missionId}/sections/{sectionId}',
      path: {
        'missionId': missionId,
        'sectionId': sectionId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Remove a section from a mission.
   * @param missionId The ID of the mission
   * @param sectionId The ID of the section
   * @returns Mission The section was successfully removed from the mission.
   * @throws ApiError
   */
  public removeMissionSection(
    missionId: UUID,
    sectionId: ID,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/missions/{missionId}/sections/{sectionId}',
      path: {
        'missionId': missionId,
        'sectionId': sectionId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Register a new patient for a mission.
   * @param missionId The ID of the mission
   * @param patientId The ID of the patient
   * @param requestBody
   * @returns Mission The patient was successfully added to the mission.
   * @throws ApiError
   */
  public createMissionPatient(
    missionId: UUID,
    patientId: ID,
    requestBody: PatientUpdate,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/missions/{missionId}/patients/{patientId}',
      path: {
        'missionId': missionId,
        'patientId': patientId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Update an existing patient.
   * @param missionId The ID of the mission
   * @param patientId The ID of the patient
   * @param requestBody
   * @returns Mission The patient was successfully updated.
   * @throws ApiError
   */
  public updateMissionPatient(
    missionId: UUID,
    patientId: ID,
    requestBody: PatientUpdate,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/missions/{missionId}/patients/{patientId}',
      path: {
        'missionId': missionId,
        'patientId': patientId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `The input data is invalid.`,
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Remove a patient from a mission.
   * @param missionId The ID of the mission
   * @param patientId The ID of the patient
   * @returns Mission The patient was successfully removed from the mission.
   * @throws ApiError
   */
  public removeMissionPatient(
    missionId: UUID,
    patientId: ID,
  ): CancelablePromise<Mission> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/missions/{missionId}/patients/{patientId}',
      path: {
        'missionId': missionId,
        'patientId': patientId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Create or update a resource across all missions.
   * The resource will be created or update across all missions that the API-Key has access to.
   *
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public postMissionResource(
    requestBody: UpdateResourceInput,
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/resources',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
      },
    });
  }

  /**
   * Get a list of all mission groups the current user has access to.
   * @returns MissionGroupList Successful response
   * @throws ApiError
   */
  public getMissionGroups(): CancelablePromise<MissionGroupList> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/groups',
    });
  }

  /**
   * Create or update a mission group
   * @param groupId The ID of the mission group
   * @param requestBody
   * @returns MissionGroup Successful response
   * @throws ApiError
   */
  public setMissionGroup(
    groupId: string,
    requestBody: MissionGroupData,
  ): CancelablePromise<MissionGroup> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/groups/{groupId}',
      path: {
        'groupId': groupId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Get all active missions
   * @param groupId The ID of the mission group
   * @param expand By default, the sub-resources `reports`, `actions` and `resources` are not included in the output. To include one or more of these sub-resources, add their names to this parameter, separated by commas.
   * @returns MissionsList Successful response
   * @throws ApiError
   */
  public getGroupMissions(
    groupId: string,
    expand?: string,
  ): CancelablePromise<MissionsList> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/groups/{groupId}/missions',
      path: {
        'groupId': groupId,
      },
      query: {
        'expand': expand,
      },
    });
  }

  /**
   * Add a mission group to a mission.
   * @param groupId The ID of the mission group.
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public addMissionToGroup(
    groupId: ID,
    requestBody: AddMissionToGroupInput,
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/groups/{groupId}/missions',
      path: {
        'groupId': groupId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
